<template>
  <div class="box-lp">
    <!-- HERO -->
    <section class="hero" style="background-image: url('/box-hero-bg.webp');">
      <div class="hero-in">
        <div class="hero-copy">
          <div class="eyebrow">{{ t('eyebrow') }}</div>
          <h1>{{ heroTitle }}</h1>
          <p class="lede">{{ heroSubtitle }}</p>
          <a href="#boxes" class="cta big">{{ t('choose_box_cta') }}</a>
          <div class="hero-trust">
            <span>{{ t('trust1') }}</span><span>{{ t('trust2') }}</span><span>{{ t('trust3') }}</span>
          </div>
        </div>
        <div class="hero-img" v-if="heroImg" :style="{ backgroundImage: `url(${heroImg})` }"></div>
      </div>
    </section>

    <!-- HOW -->
    <section class="how">
      <div class="how-step"><div class="hn">1</div><b>{{ t('how_pick_t') }}</b><span>{{ t('how_pick_d') }}</span></div>
      <div class="how-step"><div class="hn">2</div><b>{{ t('how_cook_t') }}</b><span>{{ t('how_cook_d') }}</span></div>
      <div class="how-step"><div class="hn">3</div><b>{{ t('how_deliver_t') }}</b><span>{{ t('how_deliver_d') }}</span></div>
    </section>

    <!-- BUNDLES -->
    <section id="boxes" class="boxes">
      <div class="sec-head">
        <h2>{{ t('choose_box_cta').replace(' →','') }}</h2>
        <p>{{ t('boxes_sub') }}</p>
      </div>
      <div v-if="loading" class="loadingbox">…</div>
      <div v-else class="box-grid">
        <div v-for="b in bundles" :key="b.id" class="box-card" :class="{ hero: bundleBadge(b) }" @click="openBundle(b)">
          <div class="box-hero" :style="{ backgroundImage: `url(${bundleHero(b)})` }">
            <div v-if="bundleBadge(b)" class="box-badge">{{ bundleBadge(b) }}</div>
            <div v-if="bundleSavings(b) > 0" class="box-save">{{ t('save_word') }} A${{ bundleSavings(b).toFixed(0) }}</div>
          </div>
          <div class="box-body">
            <div class="box-name">{{ bundleName(b) }}</div>
            <div class="box-tag">{{ bundleTag(b) }}</div>
            <div class="box-price">
              <div class="bp-main">
                <span class="bp-total">A${{ bundlePrice(b).toFixed(0) }}</span>
                <span v-if="bundleSavings(b) > 0" class="bp-was">A${{ bundleRegular(b).toFixed(0) }}</span>
              </div>
              <span class="bp-per">{{ bundleMeals(b) }} {{ t('meals_word') }} · A${{ perMeal(b) }}/{{ t('meals_word') }}</span>
            </div>
            <button class="cta full">{{ t('see_inside') }}</button>
          </div>
        </div>
      </div>
      <p class="promo-note">{{ promoNote }}</p>
    </section>

    <!-- FAVOURITES -->
    <section class="faves" v-if="favourites.length">
      <div class="sec-head"><h2>{{ t('faves_h') }}</h2><p>{{ t('faves_sub') }}</p></div>
      <div class="fave-grid">
        <div v-for="d in favourites" :key="d.id" class="fave" @click="openDish(d)">
          <div class="fave-img" :style="{ backgroundImage: `url(${dishImg(d)})` }"></div>
          <div class="fave-name">{{ dishName(d) }}</div>
          <div class="fave-add">+ {{ t('add_cart') }}</div>
        </div>
      </div>
    </section>

    <!-- WHY -->
    <section class="why">
      <div class="sec-head"><h2>{{ t('why_h') }}</h2></div>
      <div class="why-grid">
        <div class="why-tile"><div class="wi">🥗</div><b>{{ t('why1_t') }}</b><span>{{ t('why1_d') }}</span></div>
        <div class="why-tile"><div class="wi">👩‍🍳</div><b>{{ t('why2_t') }}</b><span>{{ t('why2_d') }}</span></div>
        <div class="why-tile"><div class="wi">⏱️</div><b>{{ t('why3_t') }}</b><span>{{ t('why3_d') }}</span></div>
        <div class="why-tile"><div class="wi">💪</div><b>{{ t('why4_t') }}</b><span>{{ t('why4_d') }}</span></div>
      </div>
    </section>

    <section class="reassure"><p>{{ t('reassure') }}</p></section>

    <!-- FAQ -->
    <section class="faq">
      <div class="sec-head"><h2>{{ t('faq_h') }}</h2></div>
      <div v-for="i in 4" :key="i" class="faq-item" @click="faqOpen = faqOpen === i ? -1 : i">
        <div class="faq-q">{{ t('faq' + i + '_q') }}<span>{{ faqOpen === i ? '−' : '+' }}</span></div>
        <div v-if="faqOpen === i" class="faq-a">{{ t('faq' + i + '_a') }}</div>
      </div>
    </section>

    <section class="final">
      <h2>{{ t('final_h') }}</h2>
      <p>{{ t('final_sub') }}</p>
      <a href="#boxes" class="cta big light">{{ t('choose_box_cta') }}</a>
    </section>

    <!-- Floating cart — opens the side cart to review before checkout -->
    <button class="box-cart-fab" v-if="cartCount" type="button" data-bs-toggle="offcanvas" data-bs-target="#boxCartCanvas" aria-label="View cart">
      <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
      <span class="fab-badge">{{ cartCount }}</span>
    </button>

    <!-- Side cart drawer -->
    <div class="offcanvas offcanvas-end" tabindex="-1" id="boxCartCanvas" aria-labelledby="boxCartCanvasLabel">
      <div class="offcanvas-header">
        <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
      </div>
      <div class="offcanvas-body">
        <CartData />
      </div>
    </div>

    <!-- MODAL -->
    <div v-if="modal.open" class="lpm" @click.self="closeModal">
      <div class="lpm-box">
        <button class="lpm-x" @click="closeModal" aria-label="Close">✕</button>
        <template v-if="modal.mode === 'bundle'">
          <div class="lpm-head">
            <div class="lpm-title">{{ bundleName(modal.bundle) }}</div>
            <div class="lpm-sub">{{ bundleMeals(modal.bundle) }} {{ t('complete_meals') }}</div>
          </div>
          <div class="lpm-scroll">
            <div class="lpm-dishlist">
              <div v-for="(d, i) in bundleItems(modal.bundle)" :key="i" class="lpm-dish">
                <div class="lpm-dimg" :style="{ backgroundImage: `url(${d.image})` }"></div>
                <div class="lpm-dinfo">
                  <div class="lpm-dname">{{ d.name }}</div>
                  <div class="lpm-macros"><span>🔥 {{ d.kcal }} kcal</span><span>💪 {{ d.protein }}g</span><span>🍚 {{ d.carbs }}g</span><span>🥑 {{ d.fat }}g</span></div>
                </div>
              </div>
            </div>
            <div v-if="desserts.length" class="lpm-extras">
              <div class="lpm-extitle">{{ t('add_treat') }} <span>(+A${{ dessertPrice }})</span></div>
              <div class="lpm-chips">
                <button v-for="ds in desserts" :key="ds.portion.id" class="lpm-chip" :class="{ on: selectedExtras.includes(ds.portion.id) }" @click="toggleExtra(ds.portion.id)">{{ dishName(ds.dish) }}</button>
              </div>
            </div>
          </div>
          <div class="lpm-foot">
            <div v-if="bundleSavings(modal.bundle) > 0" class="lpm-save">
              {{ t('save_word') }} A${{ bundleSavings(modal.bundle).toFixed(0) }} ({{ bundleSavePct(modal.bundle) }}%) <s>A${{ bundleRegular(modal.bundle).toFixed(0) }}</s>
            </div>
            <div class="lpm-total"><span>Total</span><strong>A${{ modalTotal.toFixed(2) }}</strong></div>
            <button class="cta full big" @click="addBundleFromModal">{{ t('add_box_cta') }}</button>
            <div class="lpm-note">{{ t('fully_editable') }}</div>
          </div>
        </template>
        <template v-else-if="modal.mode === 'dish'">
          <div class="lpm-dishhero">
            <span class="lpm-dishhero-blur" :style="{ backgroundImage: `url(${dishImg(modal.dish)})` }"></span>
            <img class="lpm-dishhero-img" :src="dishImg(modal.dish)" :alt="dishName(modal.dish)" />
          </div>
          <div class="lpm-head"><div class="lpm-title">{{ dishName(modal.dish) }}</div></div>
          <div class="lpm-scroll">
            <div class="lpm-portlabel">{{ t('choose_size') }}</div>
            <button v-for="p in (modal.dish.portion || [])" :key="p.id" class="lpm-port" :class="{ on: chosenPortion === p.id }" @click="chosenPortion = p.id">
              <div><div class="lpm-portname">{{ p.portion_name }}</div><div class="lpm-portmac">🔥 {{ intv(p.calories) }} kcal · 💪 {{ intv(p.protein) }}g</div></div>
              <div class="lpm-portprice">A${{ Number(p.price).toFixed(0) }}</div>
            </button>
          </div>
          <div class="lpm-foot"><button class="cta full big" :disabled="!chosenPortion" @click="addDishFromModal">{{ t('add_cart') }}</button></div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '../../axios';
import { useCartStore } from '@/stores/cart';
import { useTranslationStore } from '@/stores/translation';
import CartData from '@/components/CartData.vue';
import { useSEO } from '@/composables/useSEO';

const DESSERT_CAT = 11;

export default {
  name: 'BoxLanding',
  setup() {
    useSEO({
      title: 'CHDS Meal Boxes — Healthy Chinese Meals Delivered Melbourne',
      description: 'Ready-to-eat CHDS meal boxes: fresh, chef-cooked Chinese meals with published macros, delivered across Melbourne. Pick a box and we do the rest.',
      keywords: 'meal box Melbourne, healthy Chinese meal box, CHDS meal boxes, meal prep delivery Melbourne',
      path: '/box',
      titleZh: '餐盒 | CHDS 吃荤搭素 墨尔本健康中餐外送',
      descriptionZh: 'CHDS 餐盒：荤素搭配的中式餐食，新鲜制作，配送墨尔本全城。',
    });
  },
  components: { CartData },
  data() {
    return {
      settings: {}, bundles: [], portionMap: {}, favourites: [], desserts: [], loading: true,
      faqOpen: -1, modal: { open: false, mode: '', bundle: null, dish: null },
      selectedExtras: [], chosenPortion: null,
    };
  },
  computed: {
    cart() { return useCartStore(); },
    tr() { return useTranslationStore(); },
    lang() { return this.tr.currentLanguage; },
    cartCount() { return this.cart.totalQty; },
    heroTitle() { return this.pick(this.settings.hero_title_en, this.settings.hero_title_zh); },
    heroSubtitle() { return this.pick(this.settings.hero_subtitle_en, this.settings.hero_subtitle_zh); },
    promoNote() { return this.pick(this.settings.promo_note_en, this.settings.promo_note_zh); },
    heroImg() { return this.favourites[0] ? this.dishImg(this.favourites[0]) : ''; },
    dessertPrice() { return this.desserts[0] ? Number(this.desserts[0].portion.price).toFixed(0) : '11'; },
    modalTotal() {
      if (this.modal.mode !== 'bundle' || !this.modal.bundle) return 0;
      let t = this.bundlePrice(this.modal.bundle);
      this.selectedExtras.forEach((id) => { const e = this.portionMap[id]; if (e) t += Number(e.portion.price); });
      return t;
    },
  },
  async created() { await this.loadData(); },
  mounted() { window.addEventListener('keydown', this.onKey); },
  beforeUnmount() {
    // Never leave the page scroll-locked if we navigate away with the modal open.
    document.body.style.overflow = '';
    window.removeEventListener('keydown', this.onKey);
  },
  methods: {
    onKey(e) { if (e.key === 'Escape' && this.modal.open) this.closeModal(); },
    t(label) { return this.tr.translate(label, ['box']); },
    pick(en, zh) { return (this.lang === 'zh' && zh) ? zh : (en || ''); },
    intv(v) { return Math.round(Number(v) || 0); },
    async loadData() {
      this.loading = true;
      try { this.$route; } catch (e) { /* noop */ }
      // config
      try { const c = (await axios.get('/box-config/')).data; this.settings = c.settings || {}; this.bundles = c.bundles || []; }
      catch (e) { /* fall back to empty */ }
      // menu → portionMap + favourites + desserts
      let path = '/menu/items/'; let guard = 0;
      while (path && guard < 20) {
        guard += 1;
        let d;
        try { d = (await axios.get(path)).data; } catch (e) { break; }
        (d.results || d || []).forEach((dish) => {
          (dish.portion || []).forEach((p) => { this.portionMap[p.id] = { dish, portion: p }; });
          if ((dish.category || []).includes(DESSERT_CAT) && (dish.portion || [])[0]) this.desserts.push({ dish, portion: dish.portion[0] });
        });
        try { path = d.next ? (new URL(d.next).pathname.replace(/^\/api/, '') + new URL(d.next).search) : null; } catch (e) { path = null; }
      }
      this.desserts = this.desserts.slice(0, 8);
      // featured: config ids, else best-sellers
      const ids = this.settings.featured_dish_ids || [];
      const byId = {};
      Object.values(this.portionMap).forEach(({ dish }) => { byId[dish.id] = dish; });
      let favs = ids.map((id) => byId[id]).filter(Boolean);
      if (!favs.length) favs = Object.values(byId).filter((d) => d.is_best_selling).slice(0, 8);
      this.favourites = favs.slice(0, 8);
      this.loading = false;
    },
    dishImg(d) {
      const main = (d.item_images || []).find((i) => i.is_main) || (d.item_images || [])[0];
      return main ? 'https://api.chds.com.au' + main.image : 'https://api.chds.com.au/media/email/chds-logo.png';
    },
    dishName(d) { return this.tr.translate(d.trans_code, ['menu_item'], d.name); },
    bundleName(b) { return this.pick(b.name_en, b.name_zh); },
    bundleTag(b) { return this.pick(b.tagline_en, b.tagline_zh); },
    bundleBadge(b) { return this.pick(b.badge_en, b.badge_zh); },
    bundleHero(b) { if (b.image) return b.image; const e = this.portionMap[(b.items[0] || {}).portion_price_id]; return e ? this.dishImg(e.dish) : 'https://api.chds.com.au/media/email/chds-logo.png'; },
    bundleItems(b) {
      return (b.items || []).map((it) => {
        const e = this.portionMap[it.portion_price_id];
        if (!e) return null;
        return { name: this.dishName(e.dish), image: this.dishImg(e.dish), kcal: this.intv(e.portion.calories), protein: this.intv(e.portion.protein), carbs: this.intv(e.portion.carbs), fat: this.intv(e.portion.fats) };
      }).filter(Boolean);
    },
    // Full price if you bought the dishes separately (used for the strike-through).
    bundleRegular(b) {
      if (b && b.regular_price != null) return Number(b.regular_price);
      return (b.items || []).reduce((s, it) => { const e = this.portionMap[it.portion_price_id]; return s + (e ? Number(e.portion.price) * (it.qty || 1) : 0); }, 0);
    },
    // What the customer actually pays: the promo price if set, else the full sum.
    bundlePrice(b) { return (b && b.price != null) ? Number(b.price) : this.bundleRegular(b); },
    bundleSavings(b) { if (b && b.savings != null) return Number(b.savings); return Math.max(0, this.bundleRegular(b) - this.bundlePrice(b)); },
    bundleSavePct(b) { const r = this.bundleRegular(b); return r > 0 ? Math.round((this.bundleSavings(b) / r) * 100) : 0; },
    bundleMeals(b) { return (b.items || []).reduce((s, it) => s + (it.qty || 1), 0); },
    perMeal(b) { const m = this.bundleMeals(b) || 1; return (this.bundlePrice(b) / m).toFixed(2); },
    openBundle(b) { this.selectedExtras = []; this.modal = { open: true, mode: 'bundle', bundle: b, dish: null }; document.body.style.overflow = 'hidden'; },
    openDish(dish) { const setP = (dish.portion || []).find((p) => /set/i.test(p.portion_name || p.portion_code || '')); this.chosenPortion = (setP || (dish.portion || [])[0] || {}).id || null; this.modal = { open: true, mode: 'dish', dish, bundle: null }; document.body.style.overflow = 'hidden'; },
    closeModal() { this.modal = { open: false, mode: '', bundle: null, dish: null }; document.body.style.overflow = ''; },
    toggleExtra(id) { const i = this.selectedExtras.indexOf(id); if (i > -1) this.selectedExtras.splice(i, 1); else this.selectedExtras.push(id); },
    addSku(id, qty) { const e = this.portionMap[id]; if (!e) return; this.cart.resetActive(); this.cart.updateActivePortion(e.portion); this.cart.updateActiveQuantity(qty || 1); this.cart.addToCart({ name: e.dish.name, id: e.dish.id, trans_code: e.dish.trans_code }); },
    addBundleFromModal() {
      const b = this.modal.bundle;
      (b.items || []).forEach((it) => this.addSku(it.portion_price_id, it.qty || 1));
      this.selectedExtras.forEach((id) => this.addSku(id, 1));
      // Priced bundles carry their own coupon (customer pays the promo price); others get FIRSTBOX.
      try { localStorage.setItem('chds_promo', b.coupon_code || 'FIRSTBOX'); } catch (e) { /* noop */ }
      this.closeModal();
      // Don't jump to checkout — let them keep adding. Nudge them to the extras.
      this.$nextTick(() => this.scrollToFaves());
    },
    addDishFromModal() { if (!this.chosenPortion) return; this.addSku(this.chosenPortion, 1); try { localStorage.setItem('chds_promo', 'FIRSTBOX'); } catch (e) { /* noop */ } this.closeModal(); },
    scrollToFaves() {
      const el = this.$el.querySelector('.faves') || this.$el.querySelector('#boxes');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    goCheckout() { if (!this.cartCount) { window.location.hash = '#boxes'; return; } this.$router.push('/checkout'); },
  },
};
</script>

<style scoped>
.box-lp {
  color: #263021;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  /* Warm winter atmosphere — soft cream base with faint lime + forest blooms */
  background:
    radial-gradient(1100px 520px at 88% -6%, rgba(182, 212, 82, 0.16), transparent 60%),
    radial-gradient(900px 520px at -12% 26%, rgba(244, 221, 178, 0.34), transparent 55%),
    radial-gradient(1000px 620px at 50% 112%, rgba(68, 105, 54, 0.09), transparent 62%),
    linear-gradient(180deg, #fbfaf5 0%, #f4f6ee 100%);
}
.cta { display: inline-block; background: #446936; color: #fff; text-decoration: none; border: none; border-radius: 12px; font-weight: 700; cursor: pointer; padding: 13px 26px; font-size: 15px; transition: transform .12s, background .12s; }
.cta:hover { background: #38581c; transform: translateY(-1px); }
.cta.big { padding: 15px 34px; font-size: 17px; }
.cta.full { width: 100%; text-align: center; margin-top: 12px; }
.cta.light { background: #B6D452; color: #23400f; }
.sec-head { text-align: center; max-width: 640px; margin: 0 auto 26px; }
.sec-head h2 { font-size: 27px; font-weight: 800; margin: 0 0 8px; color: #2c3a24; }
.sec-head p { color: #6a7360; font-size: 15px; margin: 0; }
.hero { background-color: #f7f3e9; background-repeat: no-repeat; background-position: center top; background-size: cover; }
.hero-in { max-width: 1080px; margin: 0 auto; display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 30px; align-items: center; padding: 46px 20px 40px; }
.eyebrow { color: #4c6b1a; font-weight: 700; font-size: 13px; margin-bottom: 12px; }
.hero-copy h1 { font-size: 42px; line-height: 1.08; font-weight: 800; margin: 0 0 14px; color: #26331d; }
.lede { font-size: 17px; line-height: 1.5; color: #4b5442; margin: 0 0 22px; max-width: 520px; }
.hero-trust { display: flex; flex-wrap: wrap; gap: 14px; margin-top: 20px; font-size: 13px; color: #5a6350; }
.hero-img { border-radius: 20px; min-height: 320px; background-size: cover; background-position: center; box-shadow: 0 20px 50px rgba(40,60,25,0.18); }
.how { max-width: 1000px; margin: 0 auto; display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; padding: 40px 20px; }
.how-step { background: #fff; border: 1px solid #e7eadf; border-radius: 14px; padding: 20px; text-align: center; }
.how-step .hn { width: 34px; height: 34px; border-radius: 50%; background: #eef4e0; color: #4c6b1a; font-weight: 800; display: grid; place-items: center; margin: 0 auto 10px; }
.how-step b { display: block; margin-bottom: 4px; color: #2c3a24; }
.how-step span { font-size: 13px; color: #6a7360; }
.boxes { max-width: 1120px; margin: 0 auto; padding: 30px 20px 20px; }
.loadingbox { text-align: center; color: #8a9280; padding: 40px; }
.box-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
.box-card { background: #fff; border: 1px solid #e7eadf; border-radius: 16px; overflow: hidden; display: flex; flex-direction: column; position: relative; cursor: pointer; transition: box-shadow .15s, transform .15s; }
.box-card:hover { box-shadow: 0 14px 34px rgba(40,60,25,0.14); transform: translateY(-3px); }
.box-card.hero { border: 2px solid #86ad3f; box-shadow: 0 10px 30px rgba(90,130,40,0.18); }
.box-hero { aspect-ratio: 16 / 10; background-size: cover; background-position: center; background-color: #eef1e8; position: relative; }
.box-badge { position: absolute; top: 12px; left: 12px; background: #446936; color: #fff; font-size: 11px; font-weight: 800; padding: 4px 10px; border-radius: 999px; }
.box-body { padding: 14px 15px 16px; display: flex; flex-direction: column; flex: 1; }
.box-name { font-size: 18px; font-weight: 800; color: #2c3a24; }
.box-tag { font-size: 12.5px; color: #7a8370; margin-bottom: 12px; }
.box-price { display: flex; align-items: baseline; justify-content: space-between; margin-top: auto; }
.bp-main { display: flex; align-items: baseline; gap: 8px; }
.bp-total { font-size: 24px; font-weight: 800; color: #38581c; }
.bp-was { font-size: 14px; color: #a3ab97; text-decoration: line-through; }
.bp-per { font-size: 11.5px; color: #8a9280; }
.box-save { position: absolute; top: 12px; right: 12px; background: #d3462f; color: #fff; font-size: 11px; font-weight: 800; padding: 4px 10px; border-radius: 999px; box-shadow: 0 2px 8px rgba(0,0,0,0.18); }
.lpm-save { text-align: center; font-size: 13px; font-weight: 700; color: #b23522; margin-bottom: 8px; }
.lpm-save s { color: #a3ab97; font-weight: 400; margin-left: 6px; }
.promo-note { text-align: center; margin: 22px auto 0; font-size: 14px; color: #4b5442; background: #eef4e0; border-radius: 10px; padding: 12px; max-width: 560px; }
.faves { max-width: 1080px; margin: 0 auto; padding: 40px 20px; }
.fave-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
.fave { background: #fff; border: 1px solid #e7eadf; border-radius: 14px; overflow: hidden; cursor: pointer; transition: transform .12s, box-shadow .12s; }
.fave:hover { transform: translateY(-2px); box-shadow: 0 10px 24px rgba(40,60,25,0.12); }
.fave-img { aspect-ratio: 4 / 3; background-size: cover; background-position: center; background-color: #eef1e8; }
.fave-name { font-size: 13px; font-weight: 600; color: #2c3a24; padding: 10px 12px 2px; }
.fave-add { font-size: 12.5px; font-weight: 700; color: #4c6b1a; padding: 0 12px 12px; }
.why { max-width: 1080px; margin: 0 auto; padding: 30px 20px; }
.why-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
.why-tile { background: #fff; border: 1px solid #e7eadf; border-radius: 14px; padding: 20px; text-align: center; }
.why-tile .wi { font-size: 28px; }
.why-tile b { display: block; margin: 8px 0 5px; color: #2c3a24; }
.why-tile span { font-size: 13px; color: #6a7360; line-height: 1.5; }
.reassure { max-width: 780px; margin: 20px auto; padding: 0 20px; text-align: center; }
.reassure p { font-size: 17px; font-style: italic; color: #3f4a35; line-height: 1.5; }
.faq { max-width: 720px; margin: 0 auto; padding: 30px 20px; }
.faq-item { background: #fff; border: 1px solid #e7eadf; border-radius: 12px; padding: 14px 16px; margin-bottom: 10px; cursor: pointer; }
.faq-q { display: flex; justify-content: space-between; font-weight: 600; color: #2c3a24; font-size: 15px; }
.faq-q span { color: #86ad3f; font-size: 20px; line-height: 1; }
.faq-a { margin-top: 8px; font-size: 14px; color: #5a6350; line-height: 1.55; }
.final { background: linear-gradient(160deg, #446936, #38581c); color: #fff; text-align: center; padding: 50px 20px; margin-top: 20px; }
.final h2 { font-size: 28px; font-weight: 800; margin: 0 0 8px; }
.final p { color: #dcead0; margin: 0 0 22px; }
/* Floating cart button — bottom-right, always reachable */
.box-cart-fab {
  /* Sit above the global chat launcher (right:18/bottom:18, 62px) so the two
     bottom-right buttons don't overlap on desktop. */
  position: fixed; right: 18px; bottom: 92px; z-index: 50;
  width: 60px; height: 60px; border-radius: 50%; border: none; cursor: pointer;
  background: #446936; color: #fff; display: flex; align-items: center; justify-content: center;
  box-shadow: 0 10px 26px rgba(40,60,25,0.4); transition: transform .15s ease, background .15s ease;
}
.box-cart-fab:hover { background: #38581c; transform: translateY(-2px) scale(1.04); }
.box-cart-fab:active { transform: scale(0.96); }
.box-cart-fab .fab-badge {
  position: absolute; top: -4px; right: -4px; min-width: 24px; height: 24px; padding: 0 6px;
  background: #B6D452; color: #23400f; border-radius: 999px; font-size: 13px; font-weight: 800;
  display: flex; align-items: center; justify-content: center; border: 2px solid #fff;
}
@media (max-width: 760px) { .box-cart-fab { right: 14px; bottom: 88px; width: 56px; height: 56px; } }
@media (prefers-reduced-motion: reduce) { .box-cart-fab { transition: none; } }
/* z-index must beat the sticky site navbar (z-index 99) so the modal covers it
   and the close button stays reachable. */
.lpm { position: fixed; inset: 0; z-index: 2000; background: rgba(28,36,22,0.6); display: flex; align-items: flex-end; justify-content: center; -webkit-overflow-scrolling: touch; }
.lpm-box { background: #fff; width: 100%; max-width: 480px; max-height: 92vh; border-radius: 20px 20px 0 0; display: flex; flex-direction: column; position: relative; overflow: hidden; box-shadow: 0 -8px 40px rgba(0,0,0,0.25); }
.lpm-x { position: absolute; top: 12px; right: 12px; z-index: 5; width: 38px; height: 38px; border-radius: 50%; border: none; background: #fff; color: #26331d; font-size: 17px; line-height: 1; cursor: pointer; box-shadow: 0 2px 10px rgba(0,0,0,.22); display: flex; align-items: center; justify-content: center; transition: transform .15s, background .15s; }
.lpm-x:hover { background: #eef1e8; transform: scale(1.06); }
.lpm-head { padding: 20px 20px 12px; }
.lpm-title { font-size: 21px; font-weight: 800; color: #26331d; }
.lpm-sub { font-size: 13.5px; color: #7a8370; margin-top: 3px; }
.lpm-scroll { overflow-y: auto; padding: 0 20px 12px; flex: 1; }
.lpm-dishlist { display: flex; flex-direction: column; gap: 10px; }
.lpm-dish { display: flex; gap: 12px; align-items: center; }
.lpm-dimg { width: 62px; height: 62px; border-radius: 12px; background-size: cover; background-position: center; background-color: #eef1e8; flex: none; }
.lpm-dname { font-size: 14.5px; font-weight: 600; color: #2c3a24; }
.lpm-macros { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; font-size: 11.5px; color: #6a7360; }
.lpm-extras { margin-top: 18px; border-top: 1px solid #eef0e8; padding-top: 14px; }
.lpm-extitle { font-size: 14px; font-weight: 700; color: #2c3a24; margin-bottom: 10px; }
.lpm-extitle span { font-weight: 400; color: #8a9280; font-size: 12px; }
.lpm-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.lpm-chip { border: 1px solid #dfe3d5; background: #fff; color: #4a503f; border-radius: 999px; padding: 7px 13px; font-size: 12.5px; cursor: pointer; }
.lpm-chip.on { background: #446936; color: #fff; border-color: #446936; }
.lpm-dishhero { position: relative; flex: 0 0 auto; height: 230px; overflow: hidden; background: #f2f5ea; display: flex; align-items: center; justify-content: center; }
.lpm-dishhero-blur { position: absolute; inset: -24px; background-size: cover; background-position: center; filter: blur(20px) brightness(0.92); transform: scale(1.08); }
.lpm-dishhero-img { position: relative; z-index: 1; max-width: 100%; max-height: 100%; object-fit: contain; }
.lpm-portlabel { font-size: 13px; font-weight: 700; color: #2c3a24; margin: 6px 0 10px; }
.lpm-port { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 10px; border: 1.5px solid #e2e6d8; background: #fff; border-radius: 12px; padding: 11px 14px; margin-bottom: 8px; cursor: pointer; text-align: left; }
.lpm-port.on { border-color: #86ad3f; background: #f4f8ec; }
.lpm-portname { font-size: 14px; font-weight: 600; color: #2c3a24; }
.lpm-portmac { font-size: 11.5px; color: #7a8370; margin-top: 2px; }
.lpm-portprice { font-size: 16px; font-weight: 800; color: #38581c; }
.lpm-foot { padding: 14px 20px 20px; border-top: 1px solid #eef0e8; background: #fff; }
.lpm-total { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 10px; }
.lpm-total span { color: #6a7360; font-size: 14px; }
.lpm-total strong { font-size: 22px; color: #38581c; }
.cta.full.big { padding: 15px; font-size: 16px; margin-top: 0; }
.lpm-note { text-align: center; font-size: 11.5px; color: #9aa48c; margin-top: 8px; }
@media (min-width: 620px) { .lpm { align-items: center; } .lpm-box { border-radius: 20px; } }
@media (max-width: 900px) { .box-grid { grid-template-columns: repeat(2, 1fr); } .fave-grid, .why-grid { grid-template-columns: repeat(2, 1fr); } .how { grid-template-columns: 1fr; } }
@media (max-width: 760px) { .hero-in { grid-template-columns: 1fr; padding: 30px 18px; } .hero-copy h1 { font-size: 33px; } .hero-img { min-height: 200px; order: -1; } }
</style>
